import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { whenPopupFree, releasePopup, popupsBlocked } from "@/lib/launchPopups";

/**
 * First-login location permission prompt.
 *
 * Fires exactly once per user (keyed on user.id in localStorage) when:
 *   1. A user is signed in
 *   2. The browser supports geolocation
 *   3. Permission is still `prompt` (never asked or dismissed the native modal)
 *
 * Wraps the native `getCurrentPosition()` call in our own branded modal
 * so we get one clean "Turn on location" click that triggers the OS
 * dialog on a real user gesture (required by iOS Safari + some WebViews).
 *
 * Copy adapts to role: professionals + drivers see "customers can see
 * you on the map" framing, riders see "we can match you with the
 * nearest driver" framing.
 */
// Lets other first-landing popups (e.g. rating reminder) wait until this prompt is resolved.
const releaseLocPrompt = () => {
  releasePopup("location");
  if (!window.__yormLocPromptPending) return;
  window.__yormLocPromptPending = false;
  window.dispatchEvent(new Event("yorm:location-prompt-closed"));
};

export default function LocationPermissionPrompt() {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (!user?.id || !token) return;
    // Admins don't need geolocation matching — skip so the dialog overlay
    // doesn't block sidebar navigation (Analytics, App Analytics, etc.).
    if (user.role === "admin") return;
    if (typeof window === "undefined" || !navigator.geolocation) return;
    const key = `yorm_location_prompt_seen_${user.id}`;
    if (localStorage.getItem(key)) return;

    let cancelled = false;
    window.__yormLocPromptPending = true;
    const decideAndShow = async () => {
      let state = "prompt";
      // The Permissions API is not on every browser, so we fall back to
      // "prompt" and let the native dialog surface if it's still needed.
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const status = await navigator.permissions.query({ name: "geolocation" });
          state = status.state;
        }
      } catch (e) { /* ignore */ }
      if (cancelled) return;
      if (state === "granted" || state === "denied") {
        // Nothing to prompt — either it's already on, or the user
        // permanently blocked it (only OS settings can undo that).
        try { localStorage.setItem(key, "1"); } catch (e) { /* ignore */ }
        releaseLocPrompt();
        return;
      }
      // 1.6 s delay so we don't slam the modal on top of splash / other
      // first-login prompts (push permission fires at ~1.2 s).
      setTimeout(() => {
        if (cancelled) return;
        // A payment sheet opened meanwhile: step aside and wait for it to close.
        if (popupsBlocked()) { releasePopup("location"); cancelQueue = whenPopupFree("location", decideAndShow); return; }
        setOpen(true);
      }, 1600);
    };
    let cancelQueue = whenPopupFree("location", decideAndShow);
    return () => { cancelled = true; cancelQueue(); releaseLocPrompt(); };
  }, [user?.id, token]);


  const markSeen = () => {
    try {
      localStorage.setItem(`yorm_location_prompt_seen_${user.id}`, "1");
    } catch (e) { /* ignore */ }
    releaseLocPrompt();
  };

  const enable = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location services.");
      markSeen();
      setOpen(false);
      return;
    }
    setRequesting(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        toast.success("Location on.");
        markSeen();
        setRequesting(false);
        setOpen(false);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          toast.info("Location blocked · enable it in settings");
        } else {
          toast.error("Couldn't read your location. Please try again in a moment.");
        }
        markSeen();
        setRequesting(false);
        setOpen(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  const dismiss = () => {
    markSeen();
    setOpen(false);
  };

  if (!open) return null;

  const isPro = user?.role === "artisan";
  const title = isPro
    ? "Let customers see you on the map"
    : "Find drivers + professionals near you";
  const body = isPro
    ? "Yorm uses your location to show you on the customer live-fleet map and match you with nearby jobs. You can turn it off any time in your device settings."
    : "Yorm uses your location to match you with the nearest driver, show live fleet nearby, and speed up pickup. You can turn it off any time in your device settings.";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent hideCloseButton draggable aria-describedby={undefined}
        className="max-w-sm sheet-quarter"
        data-testid="location-permission-modal"
      >
        <div className="flex flex-col items-center text-center pt-1">
          <div className="w-16 h-16 rounded-full bg-[#4F988E]/10 flex items-center justify-center">
            <MapPin size={30} className="text-[#4F988E]" strokeWidth={2.25} />
          </div>
          <DialogHeader className="mt-4 space-y-2">
            <DialogTitle className="text-[22px] font-semibold tracking-[-0.02em] text-gray-900 text-center">
              {title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[15px] leading-5 text-[rgba(60,60,67,0.7)] mt-2 max-w-[300px]">{body}</p>
        </div>
        <div className="space-y-2 pt-2">
          <Button
            onClick={enable}
            disabled={requesting}
            className="w-full h-12 rounded-full bg-[#4F988E] hover:bg-[#3d7a72] active:scale-[0.98] text-white text-[17px] font-semibold transition-[background-color,transform]"
            data-testid="location-permission-enable"
          >
            {requesting ? "Turning on…" : "Turn on location"}
          </Button>
          <Button
            onClick={dismiss}
            variant="ghost"
            className="w-full h-12 rounded-full bg-[#4F988E]/10 hover:bg-[#4F988E]/20 active:scale-[0.98] text-[#4F988E] text-[17px] font-semibold transition-[background-color,transform]"
            data-testid="location-permission-skip"
          >
            Not now
          </Button>
          <p className="text-[12px] text-[rgba(60,60,67,0.6)] text-center pt-1">
            You can change this any time in your device settings.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
